/**
 * xux site selection list widget
 */
import {
  AfterViewInit,
  Component,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import { XuxGridListComponent } from '../xux-grid-list/xux-grid-list-widget.component';
import { ISite, ISiteHierarchy, ISiteHierarchyLevel } from '@shared/types';
import { Observable } from 'rxjs';
import { observableOf } from '@shared/rx';
import { XprtDataService } from '@shared/services';
import { UiActions } from '@store/actions';
import {
  differenceBy,
  find,
  forEach,
  get,
  isEmpty,
  map,
  uniqBy,
  filter,
} from 'lodash';
import { asObservable } from '@shared/utils';
import { ColDef } from 'ag-grid-community';
import { XVII18nService } from '@app/i18n/services';
import { XUX_GRID_ACTIONS, XuxGridAction } from '@geniuspos/ui-shared';
import { REST_SITES_LEVEL_NAME } from '@config/global-constants';

// eslint-disable-next-line @angular-eslint/prefer-on-push-component-change-detection
@Component({
  selector: 'xux-site-selection-list',
  templateUrl: './xux-site-selection-list.component.html',
  styleUrls: ['./xux-site-selection-list.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class XuxSiteSelectionListComponent
  extends XuxGridListComponent
  implements OnInit, AfterViewInit
{
  public siteHierarchiesList$: Observable<ISiteHierarchy[]> = observableOf([]);
  public showLoaderObs: Observable<boolean>;
  public isHierarchyApplied: boolean = false;
  public showHierarchyButton: boolean = true;
  public selectOptions: ISite[] | Observable<ISite[]> = [];
  public sites: ISite[] = [];

  public constructor(
    public uiActions: UiActions,
    private xprtDataService: XprtDataService,
    protected i18nService: XVII18nService,
  ) {
    super(uiActions, i18nService);
  }

  public ngOnInit(): void {
    const widgetData = this.field.widgetData;
    this.selectOptions = get(widgetData, 'selectOptions', []);
    this.showHierarchyButton = get(
      widgetData,
      'showHierarchyButton',
      this.showHierarchyButton,
    );
    this.showLoaderObs = asObservable(get(widgetData, 'showLoader'));
    this.subscriptions.push(
      asObservable(this.selectOptions).subscribe(
        (sites) => (this.sites = sites),
      ),
    );
    super.ngOnInit();
  }

  public ngAfterViewInit(): void {
    this.siteHierarchiesList$ = this.xprtDataService.fetchSiteHierarchies({
      filter_by_assigned_sites: true,
    });
  }

  public onHierarchyApply(value: ISiteHierarchy): void {
    this.isHierarchyApplied = !isEmpty(value);

    if (isEmpty(value)) {
      this.selectOptionsObs = asObservable(this.selectOptions);

      return;
    }

    const siteHierarchies = this.mapSitesToHierarchy(value.levels, this.sites);
    this.selectOptionsObs = observableOf(siteHierarchies);
  }

  public mapSitesToHierarchy(
    hierarchy: ISiteHierarchyLevel[],
    sitesList: ISite[],
  ): ISiteHierarchyLevel[] {
    const createGroup = (
      name: string,
      children: ISite[],
      sites: ISite[],
    ): ISiteHierarchyLevel => {
      const updatedChildren = children.map((child) => ({
        ...child,
        ...sites.find((site) => site.id === child.id),
      }));

      return {
        sites,
        children: updatedChildren,
        hierarchy: name,
        id: name,
      };
    };
    const mapSitesToLevelsRecursively = (levels): ISiteHierarchyLevel[] => {
      return map(levels, (level): ISiteHierarchyLevel => {
        if (level.id === REST_SITES_LEVEL_NAME) {
          return {
            ...level,
            children: level.children,
          };
        }

        const children = level.sites.length
          ? filter(sitesList, ({ id }) =>
              level.sites.find((site) => site.id === id),
            )
          : mapSitesToLevelsRecursively(level.children);

        return createGroup(
          level.name || level.id,
          children as ISite[],
          level.sites,
        );
      });
    };

    const groupedSites = mapSitesToLevelsRecursively(hierarchy);
    const hierarchySites = uniqBy(this.getSitesInHierarchy(hierarchy), 'id');
    const restSites = differenceBy(sitesList, hierarchySites, 'id');

    const isRequireOtherGroup = sitesList.length > hierarchySites.length;
    const otherSites = isRequireOtherGroup
      ? [createGroup(REST_SITES_LEVEL_NAME, restSites, restSites)]
      : [];

    return [...groupedSites, ...otherSites] as ISiteHierarchyLevel[];
  }

  public getSitesInHierarchy(list: ISiteHierarchyLevel[], sites = []): ISite[] {
    forEach(list, (item) => {
      if (item.sites) {
        sites.push(...item.sites);
      }
      this.getSitesInHierarchy(
        (item.children || []) as ISiteHierarchyLevel[],
        sites,
      );
    });

    return sites;
  }

  public onGridAction(action: XuxGridAction): void {
    switch (action.type) {
      case XUX_GRID_ACTIONS.GRID_READY:
        this.setSelectedNodes(this.field.control.value);
        this.updateHierarchyColumnVisibility();
        break;
      case XUX_GRID_ACTIONS.ITEMS_ARRAY_CHANGED:
        this.updateHierarchyColumnVisibility();
        break;
      default:
    }

    super.onGridAction(action);
  }

  private updateHierarchyColumnVisibility(): void {
    if (!this.showHierarchyButton) {
      return;
    }
    this.xuxGridComponent?.columnApi?.setColumnVisible(
      'hierarchy',
      this.isHierarchyApplied,
    );
    const hierarchyColumn: ColDef = find(
      this.xuxGridComponent?.columnDefinitions,
      { colId: 'hierarchy' },
    );
    hierarchyColumn.hide = !this.isHierarchyApplied;
  }
}
